import React, { useMemo, useState } from "react";
import { buildVisualizationHtml } from "../../../../utils/buildVisualizationHtml";
import { WebView } from "react-native-webview";
import {
  Modal,
  Pressable,
  StyleSheet,
  TextInput,
  View,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  useWindowDimensions,
} from "react-native";
import { XIcon, PaperPlaneTiltIcon, ChatCircleIcon, EyeIcon } from "@phosphor-icons/react";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomMarkdown from "../../../common/general/CustomMarkdown/CustomMarkdown";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import ProcessingMessageBox from "../../../chat/LoadingMessage/ProcessingMessageBox";
import {
  colors,
  radii,
  semanticColors,
  shadowStyles,
  spacing,
  typographyScale,
} from "../../../../styles/design-system";
import { getModalAnimationType } from "../../../../utils/getModalAnimationType";
import type { MessageItem } from "../../../../containers/chat/aiTutorMessage.types";

export type MessageVisualizationBlock = {
  visualizationId: string;
  visualizationType: string;
  visualizationDescription?: string | null;
  content: string;
};

export interface VisualizationModalProps {
  visible: boolean;
  block: MessageVisualizationBlock;
  messages: MessageItem[];
  inputText: string;
  isStreaming: boolean;
  onInputChange: (text: string) => void;
  onSend: () => void;
  onClose: () => void;
}

const MOBILE_BREAKPOINT = 768;

const displayTitle = (type: string) =>
  type === "interactive"
    ? "Interactive Visualization"
    : type === "nonInteractive"
      ? "Animated Visualization"
      : "Visualization";

const VisualizationModal = ({
  visible,
  block,
  messages,
  inputText,
  isStreaming,
  onInputChange,
  onSend,
  onClose,
}: VisualizationModalProps) => {
  const { width } = useWindowDimensions();
  const isMobile = width < MOBILE_BREAKPOINT;
  const [mobileTab, setMobileTab] = useState<"viz" | "chat">("viz");

  const builtContent = useMemo(
    () => (block.content ? buildVisualizationHtml(block.visualizationType, block.content) : ""),
    [block.content, block.visualizationType]
  );

  const vizRenderer = builtContent ? (
    Platform.OS === "web" ? (
      <iframe
        srcDoc={builtContent}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100%",
          height: "100%",
          border: "none",
        }}
        sandbox="allow-scripts"
        title={block.visualizationType}
      />
    ) : (
      <WebView
        source={{ html: builtContent }}
        style={styles.webView}
        javaScriptEnabled={true}
        originWhitelist={["about:", "data:"]}
        onShouldStartLoadWithRequest={(request) => {
          return request.url === "about:blank" || request.url.startsWith("data:");
        }}
        allowFileAccess={false}
        allowUniversalAccessFromFileURLs={false}
      />
    )
  ) : (
    <View style={styles.vizEmpty}>
      <CustomText
        text="No visualization content available."
        useTranslationText={false}
        size="s"
        style={styles.vizEmptyText}
      />
    </View>
  );

  const chatPanel = (
    <View style={[styles.chatPanel, isMobile && styles.chatPanelMobile]}>
      <ScrollView
        style={styles.chatScroll}
        contentContainerStyle={styles.chatScrollContent}
        showsVerticalScrollIndicator={false}>
        {messages.length === 0 ? (
          <View style={styles.chatEmptyState}>
            <CustomText
              text={block.visualizationDescription ?? "Explore the visualization and ask any questions below."}
              useTranslationText={false}
              size="s"
              style={styles.chatEmptyText}
            />
          </View>
        ) : (
          messages.map((msg) => (
            <View
              key={msg.id}
              style={[styles.chatBubble, msg.sender === "human" ? styles.chatBubbleUser : styles.chatBubbleAI]}>
              {msg.sender === "human" ? (
                <CustomText
                  text={msg.content ?? ""}
                  useTranslationText={false}
                  size="s"
                  style={styles.chatBubbleUserText}
                />
              ) : (
                <CustomMarkdown content={msg.content ?? ""} />
              )}
            </View>
          ))
        )}
        {isStreaming ? (
          <View style={styles.processingBox}>
            <ProcessingMessageBox />
          </View>
        ) : null}
      </ScrollView>

      <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : undefined}>
        <View style={styles.inputRow}>
          <TextInput
            style={styles.input}
            value={inputText}
            onChangeText={onInputChange}
            placeholder="Ask about this visualization..."
            placeholderTextColor={semanticColors.typography.textLight}
            multiline={false}
            onSubmitEditing={onSend}
            returnKeyType="send"
            editable={!isStreaming}
          />
          <CustomButton
            style={[styles.sendButton, (!inputText.trim() || isStreaming) && styles.sendButtonDisabled]}
            onPress={onSend}
            disabled={!inputText.trim() || isStreaming}
            leftIcon={(<PaperPlaneTiltIcon size={16} color={colors.white} />) as any}
            rightIcon={null}
            useTranslationText={false}
            accessibilityLabel="Send message"
            accessibilityRole="button"
          />
        </View>
      </KeyboardAvoidingView>
    </View>
  );

  return (
    <Modal visible={visible} transparent animationType={getModalAnimationType("fade")} onRequestClose={onClose}>
      <View style={[styles.backdrop, isMobile && styles.backdropMobile]}>
        <View style={[styles.modal, isMobile && styles.modalMobile]}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <CustomText
                text={displayTitle(block.visualizationType)}
                useTranslationText={false}
                size={isMobile ? "m" : "l"}
                weight="bold"
                style={styles.headerTitle}
              />
            </View>
            <Pressable
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Close visualization"
              style={styles.closeButton}>
              <XIcon size={20} color={colors.grey[600]} />
            </Pressable>
          </View>

          {/* Mobile tab bar */}
          {isMobile && (
            <View style={styles.tabBar}>
              <Pressable
                style={[styles.tab, mobileTab === "viz" && styles.tabActive]}
                onPress={() => setMobileTab("viz")}>
                <EyeIcon size={16} color={mobileTab === "viz" ? colors.blue[600] : colors.grey[500]} />
                <CustomText
                  text="Visualization"
                  useTranslationText={false}
                  size="s"
                  weight={mobileTab === "viz" ? "bold" : "regular"}
                  style={mobileTab === "viz" ? styles.tabTextActive : styles.tabText}
                />
              </Pressable>
              <Pressable
                style={[styles.tab, mobileTab === "chat" && styles.tabActive]}
                onPress={() => setMobileTab("chat")}>
                <ChatCircleIcon size={16} color={mobileTab === "chat" ? colors.blue[600] : colors.grey[500]} />
                <CustomText
                  text="Chat"
                  useTranslationText={false}
                  size="s"
                  weight={mobileTab === "chat" ? "bold" : "regular"}
                  style={mobileTab === "chat" ? styles.tabTextActive : styles.tabText}
                />
              </Pressable>
            </View>
          )}

          {/* Body */}
          {isMobile ? (
            <View style={styles.bodyMobile}>
              <View style={[styles.vizPanel, mobileTab !== "viz" && styles.hiddenPanel]}>{vizRenderer}</View>
              <View style={[{ flex: 1 }, mobileTab !== "chat" && styles.hiddenPanel]}>{chatPanel}</View>
            </View>
          ) : (
            <View style={styles.body}>
              {chatPanel}
              <View style={styles.vizPanel}>{vizRenderer}</View>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.6)",
    justifyContent: "center",
    alignItems: "center",
    padding: spacing.lg,
  },
  backdropMobile: {
    padding: 0,
  },
  modal: {
    width: "95%",
    maxWidth: 1400,
    height: "95%",
    backgroundColor: semanticColors.surface.white,
    borderRadius: radii.xl,
    overflow: "hidden",
    ...shadowStyles.lg,
  },
  modalMobile: {
    width: "100%",
    height: "100%",
    maxWidth: undefined,
    borderRadius: 0,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  headerLeft: {
    flex: 1,
  },
  headerTitle: {
    color: semanticColors.typography.displayPrimary,
  },
  closeButton: {
    padding: spacing.xs,
    borderRadius: radii.full,
  },
  tabBar: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  tab: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: spacing.xxs,
    paddingVertical: spacing.sm,
  },
  tabActive: {
    borderBottomWidth: 2,
    borderBottomColor: colors.blue[600],
  },
  tabText: {
    color: colors.grey[500],
  },
  tabTextActive: {
    color: colors.blue[600],
  },
  body: {
    flex: 1,
    flexDirection: "row",
  },
  bodyMobile: {
    flex: 1,
  },
  hiddenPanel: {
    display: "none",
  },
  chatPanel: {
    width: 400,
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  chatPanelMobile: {
    width: "100%",
    flex: 1,
    borderRightWidth: 0,
  },
  chatScroll: {
    flex: 1,
  },
  chatScrollContent: {
    padding: spacing.md,
    gap: spacing.sm,
  },
  chatEmptyState: {
    padding: spacing.md,
  },
  chatEmptyText: {
    color: semanticColors.typography.textSecondary,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  chatBubble: {
    borderRadius: radii.lg,
    padding: spacing.sm,
    maxWidth: "90%",
  },
  chatBubbleUser: {
    alignSelf: "flex-end",
    backgroundColor: colors.blue[600],
  },
  chatBubbleAI: {
    alignSelf: "flex-start",
    backgroundColor: semanticColors.surface.light,
  },
  chatBubbleUserText: {
    color: colors.white,
    fontSize: typographyScale.textSm.fontSize,
  },
  processingBox: {
    alignSelf: "flex-start",
    marginTop: spacing.xs,
    position: "relative",
    width: "100%",
    minHeight: 140,
  },
  inputRow: {
    flexDirection: "row",
    alignItems: "center",
    padding: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    gap: spacing.xs,
  },
  input: {
    flex: 1,
    backgroundColor: semanticColors.surface.light,
    borderRadius: radii.lg,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    fontSize: typographyScale.textSm.fontSize,
    color: semanticColors.typography.textPrimary,
    // @ts-expect-error - works for web
    outlineStyle: "none",
  },
  sendButton: {
    width: 36,
    height: 36,
    borderRadius: radii.full,
    backgroundColor: colors.blue[600],
    justifyContent: "center",
    alignItems: "center",
  },
  sendButtonDisabled: {
    opacity: 0.4,
  },
  vizPanel: {
    flex: 1,
    backgroundColor: semanticColors.surface.light,
    position: "relative",
    overflow: "hidden",
  },
  webView: {
    flex: 1,
  },
  vizEmpty: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  vizEmptyText: {
    color: semanticColors.typography.textSecondary,
  },
});

export default VisualizationModal;
